import type { FixedLoop } from '../core/fixedLoop';
import type { Sim } from '../sim/sim';
import type { ContentRegistry } from '../content/registry';
import { h } from './dom';
import { GAME_VERSION } from '../version';

/**
 * Debug overlay (toggle with F3 or `). Shows FPS/TPS/entity counts and exposes
 * commands: god mode, spawn N of an enemy, kill all, level up, skip stage,
 * time scale. Also exposes `window.cf` for console poking.
 */
export class DebugOverlay {
  readonly root: HTMLElement;
  private stats: HTMLElement;
  visible = false;
  private enemySelect: HTMLSelectElement;
  private countInput: HTMLInputElement;
  /** Online co-op: extra lines (ping, delay, stand-ins, resyncs, frame time) shown under the stats. */
  netInfo: (() => string[]) | null = null;
  /**
   * Online co-op guests can't run debug commands: changing one peer's sim forks
   * the run (the host's commands are repaired by the next resync instead).
   */
  commandsEnabled = true;

  constructor(
    parent: HTMLElement,
    private loop: FixedLoop,
    private reg: ContentRegistry,
    private getSim: () => Sim | null,
    private onVisibilityChange: (visible: boolean) => void = () => {},
  ) {
    this.stats = h('div', { class: 'debug-stats' });
    this.enemySelect = h('select', { style: 'font:inherit;background:#123;color:#9f9;border:1px solid #363' }) as HTMLSelectElement;
    for (const e of reg.enemyList) this.enemySelect.appendChild(h('option', { value: e.id }, e.id));
    this.countInput = h('input', { type: 'number', value: '10', style: 'width:52px;font:inherit;background:#123;color:#9f9;border:1px solid #363' }) as HTMLInputElement;
    const btn = (label: string, fn: () => void) => h('button', { class: 'small', onClick: () => (this.commandsEnabled ? fn() : undefined) }, label);
    this.root = h(
      'div',
      { id: 'debug', style: 'display:none' },
      this.stats,
      h(
        'div',
        { class: 'row' },
        btn('Close', () => this.setVisible(false)),
        btn('God', () => {
          const s = this.getSim();
          if (s) {
            s.setGodMode(!s.ctx.godMode);
          }
        }),
        btn('Kill all', () => this.getSim()?.debugKillAll()),
        btn('Level up', () => this.getSim()?.debugLevelUp()),
        btn('Skip stage', () => this.getSim()?.debugSkipStage()),
        btn('+500 XP', () => this.getSim()?.addXp(500)),
      ),
      h(
        'div',
        { class: 'row' },
        this.enemySelect,
        this.countInput,
        btn('Spawn', () => this.getSim()?.debugSpawn(this.enemySelect.value, Number(this.countInput.value) || 1)),
        btn('Spawn elite', () => this.getSim()?.debugSpawn(this.enemySelect.value, Number(this.countInput.value) || 1, true)),
        btn('Perf test', () => {
          const s = this.getSim();
          if (!s) return;
          s.setGodMode(true);
          s.debugSpawn(reg.enemyList[1]?.id ?? reg.enemyList[0].id, 120);
          s.debugSpawn(reg.enemyList[0].id, 120);
        }),
      ),
      h(
        'div',
        { class: 'row' },
        btn('0.25×', () => (this.loop.timeScale = 0.25)),
        btn('1×', () => (this.loop.timeScale = 1)),
        btn('3×', () => (this.loop.timeScale = 3)),
      ),
    );
    parent.appendChild(this.root);
    (window as unknown as { cf: unknown }).cf = { get sim() { return getSim(); }, loop, reg };
  }

  toggle(): boolean {
    this.setVisible(!this.visible);
    return this.visible;
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.root.style.display = this.visible ? '' : 'none';
    this.onVisibilityChange(this.visible);
  }

  update(drawCalls: number, triangles: number): void {
    if (!this.visible) return;
    const s = this.getSim();
    const w = s?.world;
    const lines = [
      `v${GAME_VERSION}  fps ${this.loop.fps.toFixed(0)}  tps ${this.loop.tps.toFixed(0)}  time× ${this.loop.timeScale}`,
      `draw ${drawCalls}  tris ${(triangles / 1000).toFixed(1)}k`,
      w ? `proj ${w.projectiles.pool.count}  enemies ${w.enemies.pool.count}  pickups ${w.pickups.pool.count}  hazards ${w.hazards.pool.count}` : 'no sim',
      s ? `phase ${s.run.phase}  era ${s.run.eraIndex} stage ${s.run.stageIndex}  t ${s.run.stageTime.toFixed(1)}  god ${s.ctx.godMode ? 'ON' : 'off'}  seed ${s.run.seed}` : '',
      s ? `lvl ${s.run.level} xp ${s.run.xp.toFixed(0)}/${s.run.xpToNext}  kills ${s.run.kills}  scale hp×${s.ctx.scale.hp.toFixed(2)}` : '',
      // state hash: two clients in sync show the same value every tick (docs/MULTIPLAYER.md)
      s ? `tick ${s.world.tick}  hash ${s.hashState().toString(16).padStart(8, '0')}` : '',
      ...(this.netInfo?.() ?? []),
      this.commandsEnabled ? '' : 'debug commands are host-only in online co-op',
    ];
    this.stats.textContent = lines.join('\n');
  }
}
